import { CommonModule, DecimalPipe } from '@angular/common';
import { NgModule } from '@angular/core';
import { TranslateModule } from '@ngx-translate/core';

import { BetaPipe } from './feature-pipes/beta.pipe';
import { ConstraintsPipe } from './feature-pipes/constraints.pipe';
import { ErrorPipe } from './feature-pipes/error.pipe';
import { ExecutablePipe } from './feature-pipes/executable.pipe';
import { FeatureLikePipe } from './feature-pipes/feature-like.pipe';
import { FeatureValuePipe } from './feature-pipes/feature-value.pipe';
import { FirmwarePipe } from './feature-pipes/firmware.pipe';
import { HtmlExtractorPipe } from './feature-pipes/html-extractor.pipe';
import { ModeNamePipe } from './feature-pipes/mode-name.pipe';
import { PressurePipe } from './feature-pipes/pressure.pipe';
import { ReadyPipe } from './feature-pipes/ready.pipe';
import { SensorStatusConnectedPipe } from './feature-pipes/sensor-status-connected.pipe';
import { TemperatureLevelModeNamePipe } from './feature-pipes/temperature-level-name';
import { ToFixedPipe } from './feature-pipes/to-fixed.pipe';
import { UnitPipe } from './feature-pipes/unit.pipe';
import { UnknownModeDashPipe } from './feature-pipes/unknown-mode-dash.pipe';
import { ValueStrategyModule } from './feature-pipes/value-strategy-based/value-strategy-module';
import { ValuePipe } from './feature-pipes/value.pipe';
import { GenericBatteryLevelIconPipe } from './icon-pipes/generic-battery-level-icon.pipe';
import { IconBatteryLevelCsPipe } from './icon-pipes/icon-battery-level-cs.pipe';
import { IconBatteryLevelTrvPipe } from './icon-pipes/icon-battery-level-trv.pipe';
import { IconFuelCellModePipe } from './icon-pipes/icon-fuel-cell-mode.pipe';
import { IconOperatingModePipe } from './icon-pipes/icon-operating-mode.pipe';
import { IconVentilationModePipe } from './icon-pipes/icon-ventilation-mode.pipe';
import { IconVentilationProgramPipe } from './icon-pipes/icon-ventilation-program.pipe';
import { IconWifiStrengthPipe } from './icon-pipes/icon-wifi-strength.pipe';
import { IconZigbeeDeviceWifiStrengthPipe } from './icon-pipes/icon-zigbee-device-wifi-strength.pipe';
import { IconZigbeeStrengthPipe } from './icon-pipes/icon-zigbee-strength.pipe';
import { CircuitsIndexPipe } from './label-pipes/circuits-index.pipe';
import { IdPrefixRemoverPipe } from './label-pipes/id-prefix-remover.pipe';
import { MaxLabelCharactersPipe } from './label-pipes/max-label-characters.pipe';
import { ModuleIdRemoverPipe } from './label-pipes/module-id-remover.pipe';
import { UnitsAdapterModule } from './services/units-adapter/units-adapter.module';
import { TranslateHeatingConfigurationRegulationPipe } from './translation-pipes/heating-configuration-regulation.pipe';
import { TranslateHeatingCurvePresetsPipe } from './translation-pipes/heating-curve-presets.pipe';
import { SystemTransformedUnitsPipe } from './translation-pipes/system-transformed-units.pipe';
import { TranslateAggregatedStatusPipe } from './translation-pipes/translate-aggregated-status.pipe';
import { TranslateBufferTankOperatingModePipe } from './translation-pipes/translate-buffer-tank-operating-mode.pipe';
import { TranslateBypassConfigurationPipe } from './translation-pipes/translate-bypass-configuration.pipe';
import { TranslateCascadesBurnerStatusPipe } from './translation-pipes/translate-cascades-burner-status.pipe';
import { TranslateDeviceStatusPipe } from './translation-pipes/translate-device-status.pipe';
import { TranslateDhwEcoModeLeadingPipe } from './translation-pipes/translate-dhw-eco-mode-leading.pipe';
import { TranslateDhwOperatingModePipe } from './translation-pipes/translate-dhw-operating-mode.pipe';
import { TranslateDhwScheduleModePipe } from './translation-pipes/translate-dhw-schedule-mode.pipe';
import { TranslateEcowaterDeviceVariantPipe } from './translation-pipes/translate-ecowater-device-variant.pipe';
import { TranslateEcowaterRemainingPropertyPipe } from './translation-pipes/translate-ecowater-remaining-property.pipe';
import { TranslateEnergyFlowTypePipe } from './translation-pipes/translate-energy-flow-type.pipe';
import { TranslateEssOperatingStatePipe } from './translation-pipes/translate-ess-operating-state.pipe';
import { TranslateEssSystemTypePipe } from './translation-pipes/translate-ess-system-type.pipe';
import { TranslateFuelCellModePipe } from './translation-pipes/translate-fuel-cell-mode.pipe';
import { TranslateFuelCellPhasePipe } from './translation-pipes/translate-fuel-cell-phase.pipe';
import { TranslateHeatingDeviceStatusPipe } from './translation-pipes/translate-heating-device-status.pipe';
import { TranslateHeatingSolarStatusPipe } from './translation-pipes/translate-heating-solar-status.pipe';
import { TranslateOperatingModePipe } from './translation-pipes/translate-operating-mode.pipe';
import { TranslateOperatingProgramPipe } from './translation-pipes/translate-operating-program.pipe';
import { TranslateOperatingStateDemandPipe } from './translation-pipes/translate-operating-state-demand.pipe';
import { TranslateOperatingStateModifierPipe } from './translation-pipes/translate-operating-state-modifier.pipe';
import { TranslateOperatingStatePipe } from './translation-pipes/translate-operating-state.pipe';
import { TranslatePanelNamePipe } from './translation-pipes/translate-panel-name.pipe';
import { TranslatePhotovoltaicStatePipe } from './translation-pipes/translate-photovoltaic-state.pipe';
import { TranslatePvImmersionStatusPipe } from './translation-pipes/translate-pv-immersion-status.pipe';
import { TranslateRoomHeatUpSpeedPipe } from './translation-pipes/translate-room-heat-up-speed.pipe';
import { TranslateRoomPipe } from './translation-pipes/translate-room.pipe';
import { TranslateScheduleTabPipe } from './translation-pipes/translate-schedule-tab.pipe';
import { TranslateStatePipe } from './translation-pipes/translate-state.pipe';
import { TranslateStatusPipe } from './translation-pipes/translate-status.pipe';
import { TranslateUseCaseTypePipe } from './translation-pipes/translate-use-case-type.pipe';
import { TranslateValvePositionPipe } from './translation-pipes/translate-valve-position.pipe';
import { TranslateValveTypePipe } from './translation-pipes/translate-valve-type.pipe';
import { TranslateVentilationModePipe } from './translation-pipes/translate-ventilation-mode.pipe';
import { TranslateVentilationProgramPipe } from './translation-pipes/translate-ventilation-program.pipe';
import { TranslateVentilationReasonPipe } from './translation-pipes/translate-ventilation-reason.pipe';
import { TranslateWifiStrengthPipe } from './translation-pipes/translate-wifi-strength.pipe';
import { TranslateDeviceByRolesPipe } from './translation-pipes/translateDeviceByRoles.pipe';

@NgModule({
  declarations: [
    // Features pipes
    ToFixedPipe,
    PressurePipe,
    ModeNamePipe,
    ReadyPipe,
    ExecutablePipe,
    ConstraintsPipe,
    ValuePipe,
    FeatureValuePipe,
    ErrorPipe,
    FirmwarePipe,
    UnitPipe,
    UnknownModeDashPipe,
    TemperatureLevelModeNamePipe,
    FeatureLikePipe,

    // Translation pipes
    TranslateDeviceByRolesPipe,
    TranslateEssOperatingStatePipe,
    TranslateEssSystemTypePipe,
    TranslateFuelCellModePipe,
    TranslateFuelCellPhasePipe,
    TranslateOperatingModePipe,
    TranslateDhwOperatingModePipe,
    TranslateDhwScheduleModePipe,
    TranslateDhwEcoModeLeadingPipe,
    TranslateOperatingProgramPipe,
    TranslateOperatingStatePipe,
    TranslateOperatingStateDemandPipe,
    TranslateOperatingStateModifierPipe,
    TranslatePhotovoltaicStatePipe,
    TranslateRoomPipe,
    TranslateRoomHeatUpSpeedPipe,
    TranslateStatePipe,
    TranslateScheduleTabPipe,
    TranslateStatusPipe,
    TranslateValveTypePipe,
    TranslateVentilationModePipe,
    TranslateVentilationProgramPipe,
    TranslateWifiStrengthPipe,
    TranslateValvePositionPipe,
    TranslateEnergyFlowTypePipe,
    TranslateHeatingConfigurationRegulationPipe,
    TranslateHeatingCurvePresetsPipe,
    TranslatePanelNamePipe,
    TranslateCascadesBurnerStatusPipe,
    TranslateEcowaterRemainingPropertyPipe,
    TranslateEcowaterDeviceVariantPipe,
    TranslateUseCaseTypePipe,
    TranslateBufferTankOperatingModePipe,
    HtmlExtractorPipe,
    TranslateHeatingDeviceStatusPipe,
    TranslateHeatingSolarStatusPipe,
    SystemTransformedUnitsPipe,
    TranslateVentilationReasonPipe,
    TranslatePvImmersionStatusPipe,
    TranslateBypassConfigurationPipe,

    // Icons pipes
    IconBatteryLevelCsPipe,
    IconBatteryLevelTrvPipe,
    IconFuelCellModePipe,
    IconOperatingModePipe,
    IconVentilationModePipe,
    IconVentilationProgramPipe,
    IconWifiStrengthPipe,
    IconZigbeeDeviceWifiStrengthPipe,
    IconZigbeeStrengthPipe,
    GenericBatteryLevelIconPipe,

    // Label pipes
    CircuitsIndexPipe,
    IdPrefixRemoverPipe,
    MaxLabelCharactersPipe,
    TranslateDeviceStatusPipe,
    SensorStatusConnectedPipe,
    ModuleIdRemoverPipe,
  ],
  imports: [
    CommonModule,
    TranslateModule,
    ValueStrategyModule,
    UnitsAdapterModule,
    TranslateAggregatedStatusPipe,
    BetaPipe,
  ],
  exports: [
    // Features pipes
    ToFixedPipe,
    PressurePipe,
    ModeNamePipe,
    ReadyPipe,
    ExecutablePipe,
    ConstraintsPipe,
    ValuePipe,
    FeatureValuePipe,
    ErrorPipe,
    FirmwarePipe,
    UnitPipe,
    UnknownModeDashPipe,
    TemperatureLevelModeNamePipe,
    FeatureLikePipe,
    BetaPipe,

    // Translation pipes
    TranslateAggregatedStatusPipe,
    TranslateDeviceByRolesPipe,
    TranslateEssOperatingStatePipe,
    TranslateEssSystemTypePipe,
    TranslateFuelCellModePipe,
    TranslateFuelCellPhasePipe,
    TranslateOperatingModePipe,
    TranslateDhwOperatingModePipe,
    TranslateDhwScheduleModePipe,
    TranslateDhwEcoModeLeadingPipe,
    TranslateOperatingProgramPipe,
    TranslateOperatingStatePipe,
    TranslateOperatingStateDemandPipe,
    TranslateOperatingStateModifierPipe,
    TranslatePhotovoltaicStatePipe,
    TranslatePanelNamePipe,
    TranslateRoomPipe,
    TranslateRoomHeatUpSpeedPipe,
    TranslateScheduleTabPipe,
    TranslateStatePipe,
    TranslateStatusPipe,
    TranslateValveTypePipe,
    TranslateVentilationModePipe,
    TranslateVentilationProgramPipe,
    TranslateWifiStrengthPipe,
    TranslateEnergyFlowTypePipe,
    TranslateValvePositionPipe,
    TranslateHeatingConfigurationRegulationPipe,
    TranslateHeatingCurvePresetsPipe,
    TranslateCascadesBurnerStatusPipe,
    TranslateEcowaterRemainingPropertyPipe,
    TranslateEcowaterDeviceVariantPipe,
    TranslateUseCaseTypePipe,
    TranslateBufferTankOperatingModePipe,
    HtmlExtractorPipe,
    TranslateHeatingDeviceStatusPipe,
    TranslateHeatingSolarStatusPipe,
    SystemTransformedUnitsPipe,
    TranslateVentilationReasonPipe,
    TranslatePvImmersionStatusPipe,
    TranslateBypassConfigurationPipe,

    // Icons pipes
    IconBatteryLevelCsPipe,
    IconBatteryLevelTrvPipe,
    IconFuelCellModePipe,
    IconOperatingModePipe,
    IconVentilationModePipe,
    IconVentilationProgramPipe,
    IconWifiStrengthPipe,
    IconZigbeeDeviceWifiStrengthPipe,
    IconZigbeeStrengthPipe,
    GenericBatteryLevelIconPipe,

    // Label pipes
    CircuitsIndexPipe,
    IdPrefixRemoverPipe,
    MaxLabelCharactersPipe,
    TranslateDeviceStatusPipe,
    ValueStrategyModule,
    SensorStatusConnectedPipe,
    ModuleIdRemoverPipe,
  ],
  providers: [
    IconZigbeeDeviceWifiStrengthPipe,
    ReadyPipe,
    DecimalPipe,
    FeatureValuePipe,
    TranslateRoomPipe,
  ],
})
export class OperationalDataCommonModule {}
